import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check, Shield } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'nanda_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'nanda_cookie_preferences';

const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
    };
    saveConsent(allAccepted);
  };

  const handleAcceptSelected = () => {
    saveConsent(preferences);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
    };
    saveConsent(essentialOnly);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem('nanda_consent_date', new Date().toISOString());
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Can't disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop for preferences modal */}
      {showPreferences && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9998] transition-opacity"
          onClick={() => setShowPreferences(false)}
        />
      )}

      {/* Main Banner */}
      <div className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-500 ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="bg-white border-t border-gray-200 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
            {!showPreferences ? (
              // Simple Banner View
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-[#007897]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-5 h-5 text-[#007897]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">We value your privacy</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                      By clicking &quot;Accept All&quot;, you consent to our use of cookies. 
                      Read our{' '}
                      <Link to="/privacy-policy" className="text-[#007897] hover:underline font-medium">
                        Privacy Policy
                      </Link>{' '}
                      for more information.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto">
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Customize</span>
                    <span className="sm:hidden">Settings</span>
                  </button>
                  <button
                    onClick={handleRejectNonEssential}
                    className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex items-center gap-1.5 px-4 sm:px-6 py-2 bg-[#007897] hover:bg-[#006680] text-white rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm"
                  >
                    <Check className="w-4 h-4" />
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Preferences View
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                      <Settings className="w-5 h-5 text-[#007897]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Cookie Preferences</h3>
                      <p className="text-gray-500 text-sm">Manage your cookie settings</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  {/* Essential Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-gray-900 text-sm">Essential</span>
                      </div>
                      <div className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                        Always On
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">
                      Required for the website to function properly. These cannot be disabled.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Cookie className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-gray-900 text-sm">Functional</span>
                      </div>
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          preferences.functional ? 'bg-[#007897]' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            preferences.functional ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">
                      Remember your preferences, login status, and personalization settings.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="font-medium text-gray-900 text-sm">Analytics</span>
                      </div>
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          preferences.analytics ? 'bg-[#007897]' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            preferences.analytics ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">
                      Help us understand how visitors interact with our website to improve services.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-gray-100">
                  <p className="text-gray-500 text-xs">
                    Learn more in our{' '}
                    <Link to="/privacy-policy" className="text-[#007897] hover:underline">Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/terms-of-service" className="text-[#007897] hover:underline">Terms of Service</Link>
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleRejectNonEssential}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      Essential Only
                    </button>
                    <button
                      onClick={handleAcceptSelected}
                      className="flex items-center gap-1.5 px-5 py-2 bg-[#007897] hover:bg-[#006680] text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                    >
                      <Check className="w-4 h-4" />
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;
